import { useMemo } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'

import { useCallMutation } from '@/common/api/client'
import { apiPutFetcher } from '@/common/api/fetcher'
import useSWR from 'swr'

import {
  GetAiModelsResponse,
  SystemConfigApiPath,
  UGCDispatchStatsResponse,
  UpdateConfigsResponse
} from '@/common/api/params/systemConfig'
import { useFormErrorMessages, useMessageStates } from '@/common/app/message'
import { useShowSavedToast } from '@/common/app/toast'
import { WidgetChoice } from '@/common/form/choice'
import { AiModelCatalog, AiModelTier, SystemConfig } from '@/common/model/systemConfig'
import { preventKeyDown } from '@/common/util/keyboard'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import FormCaption, { CaptionType } from '@/components/view/atoms/form/FormCaption'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Heading from '@/components/view/atoms/heading/Heading'
import { TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import FormRow from '@/components/view/layouts/form/FormRow'
import Content from '@/components/view/layouts/page/Content'
import PageBottom from '@/components/view/layouts/page/PageBottom'
import TopBar, { TopBarStatus } from '@/components/view/molecules/bar/TopBar'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import FormError from '@/components/container/form/widgets/FormError'
import FormMessageList from '@/components/container/form/widgets/FormMessageList'
import FormSelector from '@/components/container/form/widgets/FormSelector'
import FormTextField from '@/components/container/form/widgets/FormTextField'
import { useAppHeaderHiding } from '@/components/container/layout/AppHeaderProvider'

import {
  SystemConfigFormSchemaType,
  convertSystemConfigSchemaToVars,
  convertSystemConfigToSchema,
  systemConfigNameLabelMapping,
  systemConfigSchema
} from './systemConfigSchema'

interface Props {
  systemConfig: SystemConfig[]
  onRefetch(): void
}

// AI購買支援(Findee)の埋め込みプロバイダ選択肢。値は backend の EMBEDDING_PROVIDERS のキーと一致させる。
const EMBEDDING_PROVIDER_CHOICES: WidgetChoice<string>[] = [
  { name: 'stub（無料・キーワード検索のみ）', value: 'stub' },
  { name: 'Voyage voyage-3.5（推奨・意味検索/多言語）', value: 'voyage_3_5' },
  { name: 'OpenAI text-embedding-3-small', value: 'openai_small' },
  { name: 'OpenAI text-embedding-3-large', value: 'openai_large' },
  { name: 'Voyage voyage-2（旧）', value: 'voyage_2' }
]

// 送信時間帯の選択肢。開始は 0〜23時、終了は 1〜24時（終了は「その時の直前まで送る」）
const WINDOW_START_CHOICES: WidgetChoice<string>[] = Array.from({ length: 24 }, (_, h) => ({
  name: `${h}時`,
  value: String(h)
}))
const WINDOW_END_CHOICES: WidgetChoice<string>[] = Array.from({ length: 24 }, (_, i) => ({
  name: `${i + 1}時`,
  value: String(i + 1)
}))
const INTERVAL_CHOICES: WidgetChoice<string>[] = [1, 5, 10, 15, 20, 30, 60].map((m) => ({
  name: `${m}分間隔`,
  value: String(m)
}))

const AI_MODEL_TIERS: AiModelTier[] = [AiModelTier.STANDARD, AiModelTier.LIGHT]

/** 単価の目安（USD / 100万トークン）。入力・出力 */
const formatPrice = (input: number, output: number) => `入力$${input}・出力$${output}`

/**
 * AI モデルの選択肢（SMART_HUB_DEV-159）。先頭は「既定」（空文字＝既定のモデルに従う）。
 * 選択肢は API のモデル辞書から作る（フロントにモデルIDを持たない）
 */
const aiModelChoices = (catalog: AiModelCatalog, tier: AiModelTier): WidgetChoice<string>[] => {
  const defaultModel = catalog.models.find((m) => m.id === catalog.defaults[tier])
  return [
    { name: `既定（${defaultModel?.label ?? catalog.defaults[tier]}）`, value: '' },
    ...catalog.models
      .filter((m) => m.tiers.includes(tier))
      .map((m) => ({
        name: `${m.label}（${formatPrice(m.input_usd_per_mtok, m.output_usd_per_mtok)}）`,
        value: m.id
      }))
  ]
}

/** 分 → 「●時間●分」 */
const formatMinutes = (min: number) => {
  const h = Math.floor(min / 60)
  const m = Math.round(min % 60)
  return h > 0 ? `${h}時間${m}分` : `${m}分`
}

const SystemConfigForm = ({ systemConfig, onRefetch }: Props) => {
  // AI モデルの選択肢（モデル・プロダクト・既定値）。保存後は「いま使われるモデル」も取り直す
  const { data: aiModelCatalog, mutate: refetchAiModels } = useSWR<GetAiModelsResponse>(
    SystemConfigApiPath.GET_AI_MODELS
  )
  const formValues = useMemo(
    () => convertSystemConfigToSchema(systemConfig, aiModelCatalog),
    [systemConfig, aiModelCatalog]
  )
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty, isSubmitting },
    reset
  } = useForm<SystemConfigFormSchemaType>({
    resolver: zodResolver(systemConfigSchema),
    values: formValues,
    mode: 'onChange'
  })

  useAppHeaderHiding(isDirty)
  // 分散送信の試算（直近30日の受注実数）。設定値は入力中の値で計算し直す
  const { data: dispatchStats } = useSWR<UGCDispatchStatsResponse>(
    SystemConfigApiPath.GET_UGC_DISPATCH_STATS
  )
  const watchedBatch = Number(watch('ugc_mail_batch_limit')) || dispatchStats?.batch_limit || 200
  const watchedInterval =
    Number(watch('ugc_mail_interval_minutes')) || dispatchStats?.interval_minutes || 15
  const topDaily = dispatchStats?.top_site_daily_avg ?? 0
  const estMinutes = topDaily > 0 ? Math.ceil(topDaily / watchedBatch) * watchedInterval : 0
  const [messages, setMessages] = useMessageStates()
  useFormErrorMessages(errors, setMessages, systemConfigNameLabelMapping)
  const showSaved = useShowSavedToast({ editing: true })

  const { trigger } = useCallMutation<
    UpdateConfigsResponse,
    ReturnType<typeof convertSystemConfigSchemaToVars>
  >(
    SystemConfigApiPath.UPDATE_CONFIGS,
    {
      onSuccess: () => {
        showSaved('システム設定')
        onRefetch()
        refetchAiModels()
      }
    },
    apiPutFetcher
  )

  const onSubmit: SubmitHandler<SystemConfigFormSchemaType> = (data) =>
    trigger(convertSystemConfigSchemaToVars(data))

  return (
    <form onSubmit={handleSubmit(onSubmit)} onKeyDown={preventKeyDown}>
      <PageTitle
        title='システム設定'
        description='FTP取込みエラー時などの通知メール送信設定やドメイン設定を行います。'
        rightNode={
          <Button
            type='submit'
            size='large'
            color={ButtonColor.PRIMARY_BRAND}
            loading={isSubmitting}
          >
            保存する
          </Button>
        }
      />
      {isDirty && (
        <TopBar
          status={TopBarStatus.CHANGE}
          captionText='未反映の変更があります'
          onCancel={() => reset(formValues)}
          loading={isSubmitting}
        />
      )}
      <FormMessageList messageStates={messages} setMessageStates={setMessages} />
      <Content>
        <Area noBorder>
          <Heading>メール通知設定</Heading>
          <AreaContent>
            <FormRow>
              <FormLabel id='smtp_host'>SMTPサーバー</FormLabel>
              <FormRow direction='vertical' gap={2}>
                <FormTextField
                  register={register('smtp_host')}
                  error={!!errors.smtp_host?.message}
                  size={TextBoxSize.FULL}
                  placeholder='smtp.gmail.com'
                />
                <FormError message={errors.smtp_host?.message} />
              </FormRow>
            </FormRow>
            <FormRow>
              <FormLabel id='smtp_port'>SMTPポート</FormLabel>
              <FormRow direction='vertical' gap={2}>
                <FormTextField
                  register={register('smtp_port')}
                  error={!!errors.smtp_port?.message}
                  size={TextBoxSize.MEDIUM}
                  placeholder='587'
                />
                <FormError message={errors.smtp_port?.message} />
              </FormRow>
            </FormRow>
            <FormRow>
              <FormLabel id='smtp_user'>送信元メールアドレス（From）</FormLabel>
              <FormRow direction='vertical' gap={2}>
                <FormTextField
                  register={register('smtp_user')}
                  error={!!errors.smtp_user?.message}
                  size={TextBoxSize.FULL}
                  placeholder='example@gmail.com'
                />
                <FormError message={errors.smtp_user?.message} />
              </FormRow>
            </FormRow>
            <FormRow>
              <FormLabel id='notify_to'>通知先メールアドレス（To）</FormLabel>
              <FormRow direction='vertical' gap={2}>
                <FormTextField
                  register={register('notify_to')}
                  error={!!errors.notify_to?.message}
                  size={TextBoxSize.FULL}
                  placeholder='admin@example.com'
                />
                <FormError message={errors.notify_to?.message} />
              </FormRow>
            </FormRow>
            <FormRow>
              <FormLabel id='smtp_pass'>SMTPパスワード</FormLabel>
              <FormRow direction='vertical' gap={2}>
                <FormTextField
                  register={register('smtp_pass')}
                  error={!!errors.smtp_pass?.message}
                  size={TextBoxSize.FULL}
                  placeholder='パスワード'
                />
                <FormError message={errors.smtp_pass?.message} />
              </FormRow>
            </FormRow>
          </AreaContent>
        </Area>
        <Area noBorder>
          <Heading subText='W2の共通メールサーバは10万通/時の能力があります。ここはサーバ保護ではなく、深夜に送らない・1回に詰め込みすぎない、という礼儀と分散のための設定です。'>
            UGCレビュー購入者へのレビュー依頼メール（分散送信）
          </Heading>
          <AreaContent>
            <FormRow>
              <FormLabel id='ugc_mail_batch_limit'>1回の送信件数（サイトごと）</FormLabel>
              <FormRow direction='vertical' gap={2}>
                <FormTextField
                  register={register('ugc_mail_batch_limit')}
                  error={!!errors.ugc_mail_batch_limit?.message}
                  size={TextBoxSize.FULL}
                  placeholder='200'
                />
                <FormError message={errors.ugc_mail_batch_limit?.message} />
              </FormRow>
            </FormRow>
            <FormRow>
              <FormLabel id='ugc_mail_interval_minutes'>送信頻度（サイトごと）</FormLabel>
              <FormRow direction='vertical' gap={2}>
                <FormSelector
                  register={register('ugc_mail_interval_minutes')}
                  choices={INTERVAL_CHOICES}
                />
                <FormError message={errors.ugc_mail_interval_minutes?.message} />
              </FormRow>
            </FormRow>
            {dispatchStats && (
              <FormCaption type={CaptionType.SECONDARY}>
                UGC利用サイト数 {dispatchStats.ugc_site_count}件 × 1ヵ月の日の平均注文数{' '}
                {dispatchStats.daily_avg_per_site.toLocaleString()}件 ＝{' '}
                {dispatchStats.daily_total.toLocaleString()}件（日に送る対象総数）。
                最多サイト{dispatchStats.top_site_name ? `（${dispatchStats.top_site_name}）` : ''}
                の1ヵ月の日の平均注文数 {dispatchStats.top_site_daily_avg.toLocaleString()}件 ＝{' '}
                {watchedInterval}分間隔（上の設定）で {formatMinutes(estMinutes)}かかります。
              </FormCaption>
            )}
            {/* 開始・終了は対になる設定なので、縦に並べず2列で見せる */}
            <div className='grid gap-4 md:grid-cols-2'>
              <FormRow>
                <FormLabel id='ugc_mail_window_start'>送信開始時刻（JST）</FormLabel>
                <FormRow direction='vertical' gap={2}>
                  <FormSelector
                    register={register('ugc_mail_window_start')}
                    choices={WINDOW_START_CHOICES}
                  />
                  <FormError message={errors.ugc_mail_window_start?.message} />
                </FormRow>
              </FormRow>
              <FormRow>
                <FormLabel id='ugc_mail_window_end'>送信終了時刻（JST）</FormLabel>
                <FormRow direction='vertical' gap={2}>
                  <FormSelector
                    register={register('ugc_mail_window_end')}
                    choices={WINDOW_END_CHOICES}
                  />
                  <FormError message={errors.ugc_mail_window_end?.message} />
                </FormRow>
              </FormRow>
            </div>
          </AreaContent>
        </Area>
        <Area noBorder>
          <Heading>FTPサーバー設定</Heading>
          <AreaContent>
            <FormRow>
              <FormLabel id='ftp_host'>FTPサーバーIP</FormLabel>
              <FormRow direction='vertical' gap={2}>
                <FormTextField
                  register={register('ftp_host')}
                  error={!!errors.ftp_host?.message}
                  size={TextBoxSize.FULL}
                  placeholder='64.176.52.160'
                />
                <FormError message={errors.ftp_host?.message} />
              </FormRow>
            </FormRow>
          </AreaContent>
        </Area>
        <Area noBorder>
          <Heading>公開URL設定</Heading>
          <AreaContent>
            <FormRow>
              <FormLabel id='public_base_url'>公開ベースURL</FormLabel>
              <FormRow direction='vertical' gap={2}>
                <FormTextField
                  register={register('public_base_url')}
                  error={!!errors.public_base_url?.message}
                  size={TextBoxSize.FULL}
                  placeholder='https://hub.w2solution.com'
                />
                <FormError message={errors.public_base_url?.message} />
              </FormRow>
            </FormRow>
          </AreaContent>
        </Area>
        {aiModelCatalog && (
          <Area noBorder>
            <Heading subText='プロダクトごとに、AI機能で使うモデルを選びます。標準モデルは回答・分析・生成など品質が要る処理、軽量モデルは判定・分類・短い生成など速さと費用を優先する処理に使います。「既定」は推奨のモデルに従います。保存すると次のAI呼び出しから反映されます。単価は100万トークンあたりの目安（USD）です。'>
              AIモデル
            </Heading>
            <AreaContent>
              {aiModelCatalog.products.map((product) => (
                <FormRow key={product.key}>
                  <FormLabel id={`ai_model.${product.key}`}>{product.label}</FormLabel>
                  <FormRow direction='vertical' gap={2}>
                    {AI_MODEL_TIERS.filter((tier) => product.uses[tier] !== undefined).map(
                      (tier) => (
                        <FormRow key={tier} direction='vertical' gap={2}>
                          <FormCaption type={CaptionType.SECONDARY}>
                            {aiModelCatalog.tier_labels[tier]}：{product.uses[tier]}
                          </FormCaption>
                          <FormSelector
                            register={register(`ai_model.${product.key}.${tier}`)}
                            choices={aiModelChoices(aiModelCatalog, tier)}
                          />
                        </FormRow>
                      )
                    )}
                  </FormRow>
                </FormRow>
              ))}
              <FormCaption type={CaptionType.SECONDARY}>
                Opus 5.5
                は思考を止められないため応答が遅く、費用も高くなります。お客様を待たせる
                Findee の会話に使うときは応答速度に注意してください。
              </FormCaption>
            </AreaContent>
          </Area>
        )}
        <Area noBorder>
          <Heading>AI購買支援（Findee）</Heading>
          <AreaContent>
            <FormRow>
              <FormLabel id='pa_embedding_provider'>埋め込みプロバイダ</FormLabel>
              <FormRow direction='vertical' gap={2}>
                <FormSelector
                  register={register('pa_embedding_provider')}
                  choices={EMBEDDING_PROVIDER_CHOICES}
                />
                <FormCaption type={CaptionType.SECONDARY}>
                  商品レコメンドの意味検索に使うプロバイダ（システム全体の既定）。Voyage / OpenAI
                  を選ぶ場合は環境変数（VOYAGE_API_KEY
                  等）の設定が必要です。変更後は各サイトの商品ベクトルを再同期してください。
                </FormCaption>
              </FormRow>
            </FormRow>
          </AreaContent>
        </Area>
      </Content>
      <PageBottom>
        <Button type='submit' size='large' color={ButtonColor.PRIMARY_BRAND} loading={isSubmitting}>
          保存する
        </Button>
      </PageBottom>
    </form>
  )
}

export default SystemConfigForm
